/**
 * PremiumIcon — YUMIX Icon Design System (Iter. 406.fr.5)
 *
 * Source of truth for the premium icon style introduced by the Défi 7 Jours
 * row (rounded gradient container + colored glyph). All future icons across
 * Dashboard tiles, Coach YUMIX pillars, Armurerie categories, and bottom-nav
 * active state should funnel through this component to stay on-brand.
 *
 *   <PremiumIcon tone="emerald" glyph="💪" size="md" />
 *   <PremiumIcon tone="rose" glyph={<Heart className="w-5 h-5" />} size="lg" />
 *
 * `glyph` accepts an emoji string OR a React node (lucide icon). When a node
 * is provided, the icon inherits the tone's accent color via `text-{tone}-200`.
 */
import React from 'react';

const TONE_MAP = {
  emerald:   { bg: 'from-emerald-500/30 to-emerald-700/15', border: 'border-emerald-400/30', text: 'text-emerald-200', glow: 'shadow-emerald-500/20' },
  rose:      { bg: 'from-rose-500/30 to-rose-700/15', border: 'border-rose-400/30', text: 'text-rose-200', glow: 'shadow-rose-500/20' },
  orange:    { bg: 'from-orange-500/30 to-orange-700/15', border: 'border-orange-400/30', text: 'text-orange-200', glow: 'shadow-orange-500/20' },
  amber:     { bg: 'from-amber-500/30 to-amber-700/15', border: 'border-amber-400/30', text: 'text-amber-200', glow: 'shadow-amber-500/20' },
  sky:       { bg: 'from-sky-500/30 to-sky-700/15', border: 'border-sky-400/30', text: 'text-sky-200', glow: 'shadow-sky-500/20' },
  cyan:      { bg: 'from-cyan-500/30 to-cyan-700/15', border: 'border-cyan-400/30', text: 'text-cyan-200', glow: 'shadow-cyan-500/20' },
  violet:    { bg: 'from-violet-500/30 to-violet-700/15', border: 'border-violet-400/30', text: 'text-violet-200', glow: 'shadow-violet-500/20' },
  fuchsia:   { bg: 'from-fuchsia-500/30 to-fuchsia-700/15', border: 'border-fuchsia-400/30', text: 'text-fuchsia-200', glow: 'shadow-fuchsia-500/20' },
  indigo:    { bg: 'from-indigo-500/30 to-indigo-700/15', border: 'border-indigo-400/30', text: 'text-indigo-200', glow: 'shadow-indigo-500/20' },
  blue:      { bg: 'from-blue-500/30 to-blue-700/15', border: 'border-blue-400/30', text: 'text-blue-200', glow: 'shadow-blue-500/20' },
  yellow:    { bg: 'from-yellow-500/30 to-yellow-700/15', border: 'border-yellow-400/30', text: 'text-yellow-200', glow: 'shadow-yellow-500/20' },
  lime:      { bg: 'from-lime-500/30 to-lime-700/15', border: 'border-lime-400/30', text: 'text-lime-200', glow: 'shadow-lime-500/20' },
  slate:     { bg: 'from-slate-500/25 to-slate-700/10', border: 'border-slate-400/30', text: 'text-slate-200', glow: 'shadow-slate-500/15' },
};

const SIZE_MAP = {
  xs: { box: 'w-7 h-7 rounded-lg', emoji: 'text-base', node: 'w-3.5 h-3.5' },
  sm: { box: 'w-9 h-9 rounded-xl', emoji: 'text-lg', node: 'w-4 h-4' },
  md: { box: 'w-12 h-12 rounded-2xl', emoji: 'text-2xl', node: 'w-5 h-5' },
  lg: { box: 'w-14 h-14 rounded-2xl', emoji: 'text-3xl', node: 'w-6 h-6' },
  xl: { box: 'w-16 h-16 rounded-2xl', emoji: 'text-4xl', node: 'w-7 h-7' },
};

export default function PremiumIcon({
  glyph,
  tone = 'emerald',
  size = 'md',
  withGlow = false,
  className = '',
  'data-testid': testId,
  ...rest
}) {
  const t = TONE_MAP[tone] || TONE_MAP.emerald;
  const s = SIZE_MAP[size] || SIZE_MAP.md;
  const isEmoji = typeof glyph === 'string';

  const cls = [
    s.box,
    'bg-gradient-to-br',
    t.bg,
    'border',
    t.border,
    'flex items-center justify-center shrink-0',
    withGlow ? `shadow-lg ${t.glow}` : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={cls} aria-hidden="true" data-testid={testId} {...rest}>
      {isEmoji ? (
        <span className={`${s.emoji} leading-none select-none`}>{glyph}</span>
      ) : (
        // React node (lucide icon) — apply tone's accent text color so the
        // stroke picks it up unless the consumer already provided a className
        React.isValidElement(glyph)
          ? React.cloneElement(glyph, {
              className: `${s.node} ${t.text} ${glyph.props?.className || ''}`.trim(),
            })
          : glyph
      )}
    </div>
  );
}

// Convenience export — most-used tone presets so consumers don't reference strings raw
export const PREMIUM_TONES = Object.keys(TONE_MAP);
